import React from "react";
import classNames from "classnames";
import { Entity } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { CopyOnClick } from "@design-system/CopyOnClick/CopyOnClick";
import { MemoSection } from "@design-system/MemoSection/MemoSection";
import { VStack } from "@design-system/VStack/VStack";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { CopyIcon, LinkIcon } from "@icons";
import { DateOrString, formatDateOrString, getDateInputValue } from "./dates";
import { InlineDateField } from "./InlineDateField";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import styles from "./AccrualDetailsPanel.module.scss";

export interface AccrualMetadataSectionProps {
    externalStatusText?: React.ReactNode;
    statusText?: React.ReactNode;
    statusSelect?: React.ReactNode;
    /** Overrides the "Kick status" label (e.g. to "Status" for native invoices with no external system). */
    statusLabel?: string;
    counterpartyName?: string;
    counterpartyEmail?: string | null;
    accountNode?: React.ReactNode;
    recognitionScheduleNode?: React.ReactNode;
    recognitionScheduleLabel?: string;
    createdAt: DateOrString;
    dueDate?: DateOrString | null;
    onUpdateDueDate?: (dueDate: string | null) => void;
    /**
     * Native invoices only: the invoice date (YYYY-MM-DD), used to enforce
     * that the due date can never be set before the invoice date (same day
     * is allowed).
     */
    invoiceDate?: string | null;
    uncollectableAt?: DateOrString | null;
    voidedAt?: DateOrString | null;
    entity?: Entity;
    classNode?: React.ReactNode;
    documentSection: React.ReactNode;
    matchesSection?: React.ReactNode;
    memo: string | null;
    memoLabel?: string;
    onUpdateMemo?: (memo: string | null) => Promise<void>;
    onCopyUrl?: () => void;
    urlLabel?: string;
}

export const AccrualMetadataSection: React.FC<AccrualMetadataSectionProps> = ({
    externalStatusText,
    statusText,
    statusSelect,
    statusLabel = "Kick status",
    counterpartyName,
    counterpartyEmail,
    accountNode,
    recognitionScheduleNode,
    recognitionScheduleLabel = "Recognition schedule",
    createdAt,
    dueDate,
    onUpdateDueDate,
    invoiceDate,
    uncollectableAt,
    voidedAt,
    entity,
    classNode,
    documentSection,
    matchesSection,
    memo,
    memoLabel,
    onUpdateMemo,
    onCopyUrl,
    urlLabel = "Bill URL",
}) => {
    const statusValueClassName = classNames(
        styles.metadataValue,
        styles.statusValue,
    );
    const formattedCreatedAt = formatDateOrString(createdAt);
    const formattedDueDate = dueDate ? formatDateOrString(dueDate) : "-";

    return (
        <VStack gap={8}>
            {externalStatusText && (
                <DescriptionListItem
                    layout="columns"
                    label="External status"
                    valueClassName={statusValueClassName}
                >
                    {externalStatusText}
                </DescriptionListItem>
            )}

            {(statusSelect || statusText) && (
                <DescriptionListItem
                    layout="columns"
                    label={statusLabel}
                    valueClassName={statusValueClassName}
                >
                    {statusSelect ? (
                        <div className={styles.inlineSelectValue}>
                            {statusSelect}
                        </div>
                    ) : (
                        statusText
                    )}
                </DescriptionListItem>
            )}

            <InlineDateField
                label="Uncollectible date"
                value={uncollectableAt}
            />

            <InlineDateField label="Void date" value={voidedAt} />

            {counterpartyName && (
                <DescriptionListItem layout="columns" label="Counterparty">
                    {counterpartyName}
                </DescriptionListItem>
            )}

            {counterpartyEmail && (
                <DescriptionListItem layout="columns" label="Email">
                    <CopyOnClick
                        className={styles.emailValue}
                        value={counterpartyEmail}
                        tooltipText="Copy email"
                        stopPropagation
                    >
                        <span className={styles.emailText}>
                            {counterpartyEmail}
                        </span>
                        <CopyIcon className={styles.copyIcon} />
                    </CopyOnClick>
                </DescriptionListItem>
            )}

            <DescriptionListItem layout="columns" label="Created on">
                {formattedCreatedAt}
            </DescriptionListItem>

            {onUpdateDueDate ? (
                <DescriptionListItem layout="columns" label="Due date">
                    <div className={styles.datePickerContainer}>
                        <DatePicker
                            mode="day"
                            variant="panel-input"
                            clearable
                            showTriggerIcon={false}
                            placeholder="Set due date"
                            value={getDateInputValue(dueDate) || undefined}
                            minDate={invoiceDate ?? undefined}
                            onValueChange={(value) => {
                                onUpdateDueDate(value ?? null);
                            }}
                        />
                    </div>
                </DescriptionListItem>
            ) : (
                <DescriptionListItem layout="columns" label="Due date">
                    {formattedDueDate}
                </DescriptionListItem>
            )}

            {accountNode && (
                <DescriptionListItem layout="columns" label="Account">
                    {accountNode}
                </DescriptionListItem>
            )}

            {recognitionScheduleNode && (
                <DescriptionListItem
                    layout="columns"
                    label={recognitionScheduleLabel}
                >
                    {recognitionScheduleNode}
                </DescriptionListItem>
            )}

            {entity && (
                <DescriptionListItem
                    layout="columns"
                    label="Entity"
                    valueClassName={styles.entityValue}
                >
                    <EntityAvatar entity={entity} size="xs" />
                </DescriptionListItem>
            )}

            {classNode && (
                <DescriptionListItem
                    layout="columns"
                    label="Class"
                    valueClassName={styles.entityValue}
                >
                    {classNode}
                </DescriptionListItem>
            )}

            {onCopyUrl && (
                <DescriptionListItem layout="columns" label={urlLabel}>
                    <Button
                        variant="secondary"
                        iconOnly
                        size="sm"
                        onClick={onCopyUrl}
                        aria-label="Copy link"
                    >
                        <LinkIcon />
                    </Button>
                </DescriptionListItem>
            )}

            {onUpdateMemo && (
                <MemoSection
                    memo={memo}
                    onUpdateMemo={onUpdateMemo}
                    label={memoLabel}
                    labelClassName={styles.metadataLabel}
                    valueClassName={styles.metadataValue}
                />
            )}

            {documentSection}

            {matchesSection}
        </VStack>
    );
};
